import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, SpecraIconButton, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { AlertTriangle, Check, Loader2, RotateCw, X } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';
import { cn } from '../lib/utils';
import { attachmentAggregateState, canRetryAttachment, isUnreadableArchiveChild } from './FileAttachment';
import { JobStatus } from './JobStatus';
import { FileTypeIcon } from './ProjectDashboard/FileTypeIcon';
/** Attachment count at which the composer swaps cards for the compact view. */ export const COMPACT_ATTACHMENT_THRESHOLD = 4;
const stateOf = (row)=>attachmentAggregateState({
        status: row.status,
        processingStatus: row.processingStatus,
        unreadableArchiveChild: isUnreadableArchiveChild(row)
    });
const STATE_LABEL = {
    pending: 'Pending upload',
    uploading: 'Uploading',
    processing: 'Processing',
    ready: 'Ready',
    error: 'Failed'
};
export const CompactAttachmentList = ({ rows, disabled = false, onRemove, onRemoveAll, onRetry, onJobTerminal })=>{
    const [expanded, setExpanded] = useState(false);
    // The toggle only means something when the collapsed list hides chips.
    const chipsRef = useRef(null);
    const [overflowing, setOverflowing] = useState(false);
    useLayoutEffect(()=>{
        const el = chipsRef.current;
        if (!el || expanded) return;
        const measure = ()=>setOverflowing(el.scrollHeight > el.clientHeight + 1);
        measure();
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return ()=>observer.disconnect();
    }, [
        expanded,
        rows.length
    ]);
    const states = rows.map((row)=>({
            row,
            state: stateOf(row)
        }));
    // Array.sort is stable, so non-error rows keep their staging order.
    const sorted = [
        ...states
    ].sort((a, b)=>Number(b.state === 'error') - Number(a.state === 'error'));
    const count = (s)=>states.filter((x)=>x.state === s).length;
    const ready = count('ready');
    const processing = count('uploading') + count('processing');
    const waiting = count('pending');
    const failed = count('error');
    const summary = [
        `${rows.length} files`,
        ready > 0 && `${ready} ready`,
        processing > 0 && `${processing} processing`,
        waiting > 0 && `${waiting} upload when you send`,
        failed > 0 && `${failed} failed`
    ].filter(Boolean).join(' · ');
    return(// Scoped provider: the composer has no ancestor TooltipProvider to inherit.
    /*#__PURE__*/ _jsx(TooltipProvider, {
        delayDuration: 200,
        children: /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center justify-between gap-3 mb-2 text-xs text-muted-foreground",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            "data-testid": Testids.assistant.chatAttachmentSummary,
                            children: summary
                        }),
                        !disabled && /*#__PURE__*/ _jsx(Button, {
                            variant: "ghost",
                            size: "sm",
                            className: "h-6 px-2 text-xs",
                            onClick: onRemoveAll,
                            "data-testid": Testids.assistant.chatAttachmentRemoveAll,
                            children: "Remove all"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    ref: chipsRef,
                    "data-testid": Testids.assistant.chatAttachmentChips,
                    "data-expanded": expanded,
                    // Collapsed by CSS, never unmounted: every chip's JobStatus keeps
                    // polling so hidden rows still flip to ready/error.
                    className: cn('flex flex-wrap gap-1.5', expanded ? 'max-h-60 overflow-y-auto' : 'max-h-16 overflow-hidden'),
                    children: sorted.map(({ row, state })=>{
                        const canRetry = canRetryAttachment({
                            disabled,
                            hasRetry: true,
                            rejection: row.rejection,
                            sourceArchiveName: row.sourceArchiveName,
                            jobId: row.jobId,
                            status: row.status,
                            processingStatus: row.processingStatus
                        });
                        return /*#__PURE__*/ _jsxs("div", {
                            "data-testid": "chat-attachment",
                            "data-attachment-state": state,
                            className: cn('bg-muted/50 border rounded-full pl-1 pr-1 h-7 flex items-center gap-1.5 max-w-56', state === 'error' && 'border-danger/40'),
                            children: [
                                /*#__PURE__*/ _jsx(FileTypeIcon, {
                                    contentType: row.file.type,
                                    filename: row.file.name,
                                    className: "size-5 shrink-0"
                                }),
                                /*#__PURE__*/ _jsxs(Tooltip, {
                                    children: [
                                        /*#__PURE__*/ _jsx(TooltipTrigger, {
                                            asChild: true,
                                            children: /*#__PURE__*/ _jsx("span", {
                                                className: "text-xs truncate text-foreground",
                                                children: row.file.name
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(TooltipContent, {
                                            children: row.file.name
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "flex-shrink-0",
                                    role: "img",
                                    "aria-label": STATE_LABEL[state],
                                    children: state === 'ready' ? /*#__PURE__*/ _jsx(Check, {
                                        className: "w-3.5 h-3.5 text-success"
                                    }) : state === 'error' ? /*#__PURE__*/ _jsx(AlertTriangle, {
                                        className: "w-3.5 h-3.5 text-danger"
                                    }) : /*#__PURE__*/ _jsx(Loader2, {
                                        className: "w-3.5 h-3.5 animate-spin text-muted-foreground"
                                    })
                                }),
                                canRetry && /*#__PURE__*/ _jsx(SpecraIconButton, {
                                    label: "Retry upload",
                                    size: "sm",
                                    variant: "ghost",
                                    className: "size-5 flex-shrink-0 rounded-full",
                                    onClick: ()=>onRetry(row.id),
                                    "data-testid": "chat-attachment-retry",
                                    children: /*#__PURE__*/ _jsx(RotateCw, {
                                        className: "w-3.5 h-3.5 text-muted-foreground hover:text-primary"
                                    })
                                }),
                                !disabled && /*#__PURE__*/ _jsx(SpecraIconButton, {
                                    label: "Remove file",
                                    size: "sm",
                                    variant: "ghost",
                                    className: "size-5 flex-shrink-0 rounded-full",
                                    onClick: ()=>onRemove(row.id),
                                    children: /*#__PURE__*/ _jsx(X, {
                                        className: "w-3.5 h-3.5 text-muted-foreground hover:text-danger"
                                    })
                                }),
                                !isUnreadableArchiveChild(row) && row.status === 'uploaded' && row.jobId && row.processingStatus && row.processingStatus !== 'none' && /*#__PURE__*/ _jsx("div", {
                                    className: "hidden",
                                    children: /*#__PURE__*/ _jsx(JobStatus, {
                                        jobId: row.jobId,
                                        enabled: true,
                                        showCancel: false,
                                        onComplete: ()=>onJobTerminal(row.id, 'completed'),
                                        onError: ()=>onJobTerminal(row.id, 'failed'),
                                        onCancel: ()=>onJobTerminal(row.id, 'cancelled')
                                    })
                                })
                            ]
                        }, row.id);
                    })
                }),
                (overflowing || expanded) && /*#__PURE__*/ _jsx(Button, {
                    variant: "link",
                    size: "sm",
                    className: "mt-1 h-6 px-0 text-xs",
                    "aria-expanded": expanded,
                    onClick: ()=>setExpanded((v)=>!v),
                    "data-testid": Testids.assistant.chatAttachmentToggle,
                    children: expanded ? 'Show less' : `Show all ${rows.length} files`
                })
            ]
        })
    }));
};
